/**
 * Auth Module — OIDC Authorization Code flow with PKCE.
 *
 * Manages login initiation, callback handling, session refresh,
 * logout, and session verification. Uses Web Crypto API for
 * PKCE generation and sessionStorage for flow state persistence.
 */

import { apiClient } from '@/modules/api';
import { useAuthStore, UserProfile } from '@/stores/auth-store';

// --- Types ---

export interface OIDCConfig {
  clientId: string;
  issuerUrl: string;
  scopes: string[];
  authorizationEndpoint: string;
}

export interface AuthResult {
  success: boolean;
  error?: string;
  returnPath?: string;
}

interface PKCEState {
  codeVerifier: string;
  state: string;
}

// --- Constants ---

const PKCE_STORAGE_KEY = 'auth_pkce_verifier';
const STATE_STORAGE_KEY = 'auth_state';
const RETURN_PATH_KEY = 'auth_return_path';

/** Unreserved URI characters allowed in PKCE verifier */
const UNRESERVED_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';

/** Default verifier length (within 43-128 range) */
const VERIFIER_LENGTH = 64;

/** Minimum state parameter entropy in bytes */
const STATE_BYTES = 32;

/** Token exchange timeout in ms */
const TOKEN_TIMEOUT_MS = 10_000;

// --- PKCE Utilities ---

/**
 * Generates a cryptographically random code verifier between 43-128 characters
 * using only unreserved URI characters [A-Za-z0-9\-._~].
 */
export function generateCodeVerifier(length: number = VERIFIER_LENGTH): string {
  if (length < 43 || length > 128) {
    throw new Error('Code verifier length must be between 43 and 128');
  }

  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);

  let verifier = '';
  for (let i = 0; i < length; i++) {
    verifier += UNRESERVED_CHARS[randomValues[i]! % UNRESERVED_CHARS.length];
  }

  return verifier;
}

/**
 * Computes the S256 code challenge from a code verifier.
 * challenge = BASE64URL(SHA-256(verifier))
 */
export async function computeCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(digest);
}

/**
 * BASE64URL encoding (no padding) of an ArrayBuffer.
 * Replaces + with -, / with _, and strips trailing =.
 */
export function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Generates a cryptographically random state parameter with at least 32 bytes of entropy.
 * Returns a BASE64URL-encoded string.
 */
export function generateState(byteLength: number = STATE_BYTES): string {
  if (byteLength < 32) {
    throw new Error('State parameter must have at least 32 bytes of entropy');
  }

  const randomBytes = new Uint8Array(byteLength);
  crypto.getRandomValues(randomBytes);
  return base64UrlEncode(randomBytes.buffer);
}

/**
 * Validates that the received state matches the stored state (byte-for-byte).
 */
export function validateState(stored: string, received: string): boolean {
  if (stored.length !== received.length) {
    return false;
  }
  // Constant-time comparison to prevent timing attacks
  let mismatch = 0;
  for (let i = 0; i < stored.length; i++) {
    mismatch |= stored.charCodeAt(i) ^ received.charCodeAt(i);
  }
  return mismatch === 0;
}

// --- SessionStorage Helpers ---

function storePKCEState(pkce: PKCEState): void {
  sessionStorage.setItem(PKCE_STORAGE_KEY, pkce.codeVerifier);
  sessionStorage.setItem(STATE_STORAGE_KEY, pkce.state);
}

function retrievePKCEState(): PKCEState | null {
  const codeVerifier = sessionStorage.getItem(PKCE_STORAGE_KEY);
  const state = sessionStorage.getItem(STATE_STORAGE_KEY);

  if (!codeVerifier || !state) {
    return null;
  }

  return { codeVerifier, state };
}

function clearPKCEState(): void {
  sessionStorage.removeItem(PKCE_STORAGE_KEY);
  sessionStorage.removeItem(STATE_STORAGE_KEY);
}

export function storeReturnPath(path: string): void {
  sessionStorage.setItem(RETURN_PATH_KEY, path);
}

export function retrieveReturnPath(): string | null {
  return sessionStorage.getItem(RETURN_PATH_KEY);
}

export function clearReturnPath(): void {
  sessionStorage.removeItem(RETURN_PATH_KEY);
}

// --- Auth Module ---

/**
 * Fetches the OIDC configuration from the auth service.
 */
export async function fetchOIDCConfig(): Promise<OIDCConfig> {
  return apiClient.get<OIDCConfig>('/auth/config', { skipAuthIntercept: true });
}

/**
 * Initiates the OIDC login flow:
 * 1. Fetches OIDC config from GET /auth/config
 * 2. Generates PKCE pair (verifier + S256 challenge)
 * 3. Generates state parameter (32+ bytes entropy)
 * 4. Stores verifier and state in sessionStorage
 * 5. Redirects browser to authorization endpoint
 */
export async function initiateLogin(): Promise<void> {
  const config = await fetchOIDCConfig();

  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await computeCodeChallenge(codeVerifier);
  const state = generateState();

  storePKCEState({ codeVerifier, state });

  const redirectUri = `${window.location.origin}/auth/callback`;
  const scopes = config.scopes.join(' ');

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: redirectUri,
    scope: scopes,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });

  const authUrl = `${config.authorizationEndpoint}?${params.toString()}`;
  window.location.href = authUrl;
}

/**
 * Handles the /auth/callback redirect:
 * 1. Checks for error parameter in URL
 * 2. Validates state parameter matches stored state
 * 3. Exchanges authorization code for session via POST /auth/token
 * 4. Clears PKCE state from sessionStorage
 * 5. Updates auth store to authenticated state
 */
export async function handleCallback(params: URLSearchParams): Promise<AuthResult> {
  // Check for error response from IdP
  const error = params.get('error');
  if (error) {
    const errorDescription = params.get('error_description') || 'Authentication failed';
    clearPKCEState();
    return { success: false, error: errorDescription };
  }

  const code = params.get('code');
  const receivedState = params.get('state');

  if (!code || !receivedState) {
    clearPKCEState();
    return { success: false, error: 'Missing authorization code or state parameter' };
  }

  // Retrieve stored PKCE state
  const storedPKCE = retrievePKCEState();
  if (!storedPKCE) {
    return { success: false, error: 'No stored authentication state found. Please try logging in again.' };
  }

  // Validate state parameter
  if (!validateState(storedPKCE.state, receivedState)) {
    clearPKCEState();
    return { success: false, error: 'State parameter mismatch. The authentication request could not be verified.' };
  }

  // Exchange code for session
  try {
    const tokenResponse = await apiClient.post<{ user: UserProfile }>(
      '/auth/token',
      { code, codeVerifier: storedPKCE.codeVerifier },
      { skipAuthIntercept: true, timeout: TOKEN_TIMEOUT_MS },
    );

    // Clear PKCE state on success
    clearPKCEState();

    // Update auth store
    useAuthStore.getState().setAuthenticated(tokenResponse.user);

    // Retrieve and clear return path
    const returnPath = retrieveReturnPath();
    clearReturnPath();

    return { success: true, returnPath: returnPath || undefined };
  } catch (err) {
    clearPKCEState();

    const message = err instanceof Error ? err.message : 'Token exchange failed';
    if (message.includes('timeout') || message.includes('Timeout')) {
      return { success: false, error: 'Authentication timed out. Please try again.' };
    }
    return { success: false, error: message };
  }
}

/**
 * Refreshes the session by calling POST /auth/refresh.
 * On failure, clears auth state.
 */
export async function refreshSession(): Promise<void> {
  await apiClient.post('/auth/refresh', undefined, { skipAuthIntercept: true });
}

/**
 * Logs out the user:
 * 1. Calls POST /auth/logout
 * 2. Clears auth store state (even if the request fails)
 */
export async function logout(): Promise<void> {
  try {
    await apiClient.post('/auth/logout', undefined, { skipAuthIntercept: true });
  } catch {
    // Swallow errors — we always clear local state regardless
  } finally {
    useAuthStore.getState().setUnauthenticated();
  }
}

/**
 * Verifies the current session by calling GET /auth/me.
 * Returns the user profile on success.
 * On 401, clears auth state.
 */
export async function verifySession(): Promise<UserProfile> {
  const user = await apiClient.get<UserProfile>('/auth/me', {
    skipAuthIntercept: true,
    timeout: 5_000,
  });
  useAuthStore.getState().setAuthenticated(user);
  return user;
}
